import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import Avatar from '../Avatar';
import { orgLabel } from '../../utils/orgTaxonomy';
import WithPresence from '../PresenceDot';
import { useRecentPicks } from '../../utils/recentPicks';
import { RecentPickLabel, RecentPickDivider } from '../RecentPickLabel';

/**
 * Choose colleagues — used for adding to a group and for starting a new chat.
 *
 * Everyone active is listed, unscoped. Task assignment is scoped by team
 * (utils/taskScoping.js), but management's rule for chat is deliberately flat:
 * anyone may message anyone. A picker that hid people would quietly contradict
 * that, and the person you cannot find is the reason someone reaches for
 * WhatsApp instead.
 */
const PeoplePicker = ({
    title = 'Select people',
    excludeIds = [],
    multi = true,
    /*
     * A ceiling on how many may be picked, for callers that have one.
     *
     * Infinity by default, so every existing call site behaves exactly as it
     * did. A call has a hard participant cap, and letting somebody tick five
     * people and only then refusing the whole batch is a worse interaction than
     * stopping them at the limit — which is knowable before they start.
     */
    maxSelect = Infinity,
    /*
     * A fixed list to choose from, instead of the whole directory.
     *
     * Used when the choice is already bounded — picking who to ring out of a
     * group's own membership, where searching the company would offer people the
     * group does not contain. Omitted, it searches everyone, which is what every
     * other caller wants.
     */
    people: fixedPeople = null,
    confirmLabel,
    // Which recent picks to show first (utils/recentPicks.js).
    recentKey = 'chat-people',
    onConfirm,
    onCancel
}) => {
    const [people, setPeople] = useState([]);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const searchRef = useRef(null);
    const recent = useRecentPicks(recentKey);

    useEffect(() => {
        if (fixedPeople) {
            const q = query.trim().toLowerCase();
            setPeople(q
                ? fixedPeople.filter((p) => (p.name || '').toLowerCase().includes(q))
                : fixedPeople);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;

        // Debounced: a request per keystroke against a 500-person directory is
        // a lot of round trips for a list that barely changes.
        const t = setTimeout(async () => {
            try {
                const res = await api.get('/conversations/contacts', { params: { q: query } });
                if (!cancelled) setPeople(res.data || []);
            } catch {
                if (!cancelled) setPeople([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? 250 : 0);

        return () => { cancelled = true; clearTimeout(t); };
    }, [query, fixedPeople]);

    const exclude = new Set(excludeIds.map(String));
    const visible = people.filter((p) => !exclude.has(String(p._id)));

    const atLimit = selected.length >= maxSelect;

    // With nothing typed, the people picked here lately come first.
    const { recent: recentRows, rest: otherRows } = query.trim()
        ? { recent: [], rest: visible }
        : recent.split(visible);

    const renderPerson = (p) => (
        <div
            key={p._id}
            className={[
                'msgr-pick',
                selected.includes(p._id) && 'selected',
                atLimit && !selected.includes(p._id) && 'is-disabled'
            ].filter(Boolean).join(' ')}
            onClick={() => toggle(p._id)}
        >
            <WithPresence userId={p._id} small>
                <Avatar name={p.name} profilePic={p.profilePic} className="msgr-avatar" />
            </WithPresence>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{p.name}</div>
                <div style={{ fontSize: 11.5, color: 'light-dark(#667781, var(--theme-muted))' }}>
                    {[orgLabel(p.jobTitle) || p.role, orgLabel(p.department)].filter(Boolean).join(' · ')}
                </div>
            </div>
            {selected.includes(p._id) && (
                <FontAwesomeIcon icon={faCheck} style={{ color: 'light-dark(#128c7e, var(--theme-success-ink, #7bcf9f))' }} />
            )}
        </div>
    );

    const toggle = (id) => {
        if (!selected.includes(id)) recent.record(id);
        if (!multi) { onConfirm([id]); return; }
        setSelected((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= maxSelect) return prev;
            return [...prev, id];
        });
        // Ready for the next name: the search empties and keeps the cursor.
        setQuery('');
        searchRef.current?.focus();
    };

    return (
        <div className="msgr-modal-back" onClick={onCancel}>
            <div className="msgr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="msgr-modal-head">
                    <h4>{title}</h4>
                    <button className="msgr-icon-btn" onClick={onCancel}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="msgr-modal-body">
                    <div className="msgr-search" style={{ marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="msgr-search-icon" />
                        <input
                            ref={searchRef}
                            autoFocus
                            placeholder="Search by name, ID or department"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            style={{ border: '1px solid light-dark(#e4e6eb, var(--theme-border, #33363b))' }}
                        />
                    </div>

                    <div className="msgr-picklist">
                        {loading && <div className="msgr-empty">Loading…</div>}
                        {!loading && !visible.length && (
                            <div className="msgr-empty">Nobody matches that.</div>
                        )}
                        {!loading && recentRows.length > 0 && <RecentPickLabel />}
                        {!loading && recentRows.map(renderPerson)}
                        {!loading && recentRows.length > 0 && otherRows.length > 0 && <RecentPickDivider />}
                        {otherRows.map(renderPerson)}
                    </div>
                </div>

                {multi && (
                    <div className="msgr-modal-foot">
                        {Number.isFinite(maxSelect) && (
                            <span className="msgr-pick-limit">
                                {atLimit
                                    ? 'That is as many as will fit'
                                    : `${maxSelect - selected.length} more can join`}
                            </span>
                        )}
                        <button className="msgr-btn" onClick={onCancel}>Cancel</button>
                        <button
                            className="msgr-btn primary"
                            disabled={!selected.length}
                            onClick={() => onConfirm(selected)}
                        >
                            {confirmLabel || `Add ${selected.length || ''}`.trim()}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PeoplePicker;
